import {
    AlertCircle,
    ArrowLeft,
    Check,
    CreditCard,
    Loader2,
    Sparkles,
    FileText,
    Building2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { useUserProfile } from '../context/UserProfileContext'
import type { PricingPlan } from '../types'
import { ApiError } from '../utils/api'
import paymentService from '../utils/paymentService'

const formatCurrency = (amount: number, currency: string = 'GBP'): string => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
    }).format(amount)
}

export default function PricingPage() {
    const [plans, setPlans] = useState<Record<string, PricingPlan[]>>({})
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
    const [activeTab, setActiveTab] = useState<string>('self-assessment')

    const { user } = useUserProfile()
    const navigate = useNavigate()

    useEffect(() => {
        loadPricingPlans()
    }, [])

    const loadPricingPlans = async () => {
        try {
            setIsLoading(true)
            setError(null)
            const plansData = await paymentService.listPricingPlans()
            
            // plansData is already the correct structure: { "self-assessment": [...], "ct600": [...] }
            setPlans(plansData as unknown as Record<string, PricingPlan[]>)
            
            // Set active tab to first available product type
            const firstProductType = Object.keys(plansData)[0]
            if (firstProductType) {
                setActiveTab(firstProductType)
            }
        } catch (err) {
            console.error('Failed to load pricing plans:', err)
            if (err instanceof ApiError) {
                setError(err.message || 'Unable to load pricing plans.')
            } else {
                setError('Unable to load pricing plans. Please try again.')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleCheckout = async (plan: PricingPlan) => {
        try {
            setCheckoutLoading(plan.slug)
            setError(null)

            // Build redirect URLs with session ID placeholder for Stripe
            const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&plan=${plan.slug}`
            const cancelUrl = `${window.location.origin}/payment/cancelled`

            // Create checkout session via payment service
            const checkoutData = await paymentService.createCheckout(plan.slug, {
                successUrl,
                cancelUrl,
                customerEmail: user?.email || '',
            })

            // Validate checkout URL before redirect
            if (!checkoutData.url) {
                throw new Error('Invalid checkout session URL')
            }

            // Log checkout initiation for debugging
            console.log('Initiating checkout:', {
                planSlug: plan.slug,
                sessionId: checkoutData.id,
            })

            // Redirect to Stripe-hosted checkout page
            window.location.href = checkoutData.url
        } catch (err) {
            console.error('Checkout failed:', err)
            if (err instanceof ApiError) {
                setError(err.message || 'Unable to start checkout. Please try again.')
            } else {
                setError('Unable to start checkout. Please try again.')
            }
            setCheckoutLoading(null)
        }
    }

    const renderPricingCard = (plan: PricingPlan, isPopular: boolean = false) => {
        const isProcessing = checkoutLoading === plan.slug

        return (
            <div
                key={plan.slug}
                className={`relative bg-white rounded-2xl p-8 shadow-sm border transition-all duration-200 ${isPopular ? 'border-red-500 ring-2 ring-red-500 shadow-lg scale-105' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    } h-full flex flex-col`}
            >
                {isPopular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-red-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-md">
                            Most Popular
                        </span>
                    </div>
                )}

                {/* Plan Header */}
                <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className={`h-5 w-5 ${isPopular ? 'text-red-500' : 'text-gray-400'}`} />
                        <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                    </div>
                </div>

                {/* Price */}
                <div className="mb-8">
                    <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-gray-900">
                            {formatCurrency(plan.price, plan.currency)}
                        </span>
                        {plan.billingCycle !== 'one-off' && (
                            <span className="text-gray-500 text-sm">/{plan.billingCycle}</span>
                        )}
                    </div>
                    {plan.billingCycle === 'one-off' && (
                        <p className="text-sm text-gray-600 mt-1">One-time payment</p>
                    )}
                </div>

                {/* Features */}
                <div className="mb-8 flex-grow">
                    <h4 className="text-gray-900 font-medium mb-4">What&apos;s included:</h4>
                    <ul className="space-y-3">
                        {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                                <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isPopular ? 'bg-red-100' : 'bg-green-100'}`}>
                                    <Check className={`h-3 w-3 ${isPopular ? 'text-red-600' : 'text-green-600'}`} />
                                </div>
                                <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Button */}
                <button
                    onClick={() => handleCheckout(plan)}
                    disabled={isProcessing}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${isPopular
                            ? 'bg-red-500 text-white hover:bg-red-600 shadow-md hover:shadow-lg disabled:bg-gray-300'
                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:bg-gray-100'
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                    {isProcessing ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <CreditCard className="h-4 w-4" />
                            Get Started
                        </>
                    )}
                </button>
            </div>
        )
    }

    const getTabIcon = (productType: string) => {
        switch (productType) {
            case 'self-assessment':
                return FileText
            case 'ct600':
                return Building2
            default:
                return Sparkles
        }
    }

    const getTabLabel = (productType: string) => {
        switch (productType) {
            case 'self-assessment':
                return 'Self Assessment'
            case 'ct600':
                return 'CT600'
            default:
                return productType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        }
    }

    const scrollContainer = (direction: 'left' | 'right') => {
        const container = document.querySelector('.pricing-scroll-container')
        if (container) {
            const scrollAmount = 350 // Width of card + gap
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            })
        }
    }

    const renderTabContent = (productPlans: PricingPlan[]) => {
        // Sort by sortOrder
        const sortedPlans = [...productPlans].sort((a, b) => a.sortOrder - b.sortOrder)

        // Determine popular plan (middle one or second one)
        const popularIndex = sortedPlans.length > 1 ? 1 : 0

        // Use horizontal scroll for CT600 plans (more than 3 plans)
        const useHorizontalScroll = sortedPlans.length > 3

        if (useHorizontalScroll) {
            return (
                <div className="relative group max-w-7xl mx-auto py-8">
                    {/* Scroll button - left */}
                    <button
                        onClick={() => scrollContainer('left')}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full shadow-lg p-3 hover:bg-gray-50 ml-4"
                        aria-label="Scroll left"
                    >
                        <ArrowLeft className="h-5 w-5 text-red-500" />
                    </button>
                    
                    {/* Scroll button - right */}
                    <button
                        onClick={() => scrollContainer('right')}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white rounded-full shadow-lg p-3 hover:bg-gray-50 mr-4"
                        aria-label="Scroll right"
                    >
                        <ArrowLeft className="h-5 w-5 text-red-500 rotate-180" />
                    </button>

                    {/* Scrollable container */}
                    <div className="overflow-x-auto scrollbar-hide pricing-scroll-container pb-6 pt-6">
                        <div className="flex gap-8 px-4">
                            {sortedPlans.map((plan, index) => (
                                <div key={plan.slug} className="w-80 flex-shrink-0">
                                    {renderPricingCard(plan, index === popularIndex)}
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Scroll hint text */}
                    <div className="text-center text-sm text-gray-500">
                        ← Scroll to see all plans →
                    </div>
                </div>
            )
        }

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {sortedPlans.map((plan, index) => renderPricingCard(plan, index === popularIndex))}
            </div>
        )
    }

    return (
        <AuthenticatedLayout>
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-8">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Dashboard
                        </button>
                        <div className="text-center">
                            <h1 className="text-4xl font-bold text-gray-900 mb-4">
                                Simple, Transparent Pricing
                            </h1>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                                Choose a plan that fits your needs. No hidden fees, no surprises.
                            </p>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-8 max-w-4xl mx-auto">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm text-red-800">{error}</p>
                                    <button
                                        onClick={loadPricingPlans}
                                        className="mt-2 text-sm font-medium text-red-600 hover:text-red-700"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Loading State */}
                    {isLoading && (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        </div>
                    )}

                    {/* Tabs */}
                    {!isLoading && !error && Object.keys(plans).length > 0 && (
                        <div className="mb-12">
                            <div className="flex justify-center gap-4 flex-wrap">
                                {Object.keys(plans).map((productType) => {
                                    const Icon = getTabIcon(productType)
                                    const isActive = activeTab === productType
                                    
                                    return (
                                        <button
                                            key={productType}
                                            onClick={() => setActiveTab(productType)}
                                            className={`flex items-center gap-3 px-8 py-4 rounded-xl font-medium transition-all duration-200 ${
                                                isActive
                                                    ? 'bg-red-500 text-white shadow-lg scale-105'
                                                    : 'bg-white text-gray-700 border border-gray-200 hover:border-red-200 hover:bg-red-50'
                                            }`}
                                        >
                                            <Icon className="h-5 w-5" />
                                            <span>{getTabLabel(productType)}</span>
                                        </button>
                                    )
                                })}
                            </div>
                            <div className="text-center mt-6">
                                <p className="text-gray-600">
                                    Choose the plan that fits your needs. All plans include HMRC integration and secure filing.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Pricing Plans */}
                    {!isLoading && !error && Object.keys(plans).length === 0 && (
                        <div className="text-center py-20">
                            <p className="text-gray-500">No pricing plans available at the moment.</p>
                        </div>
                    )}

                    {!isLoading && !error && plans[activeTab] && (
                        <div className="animate-fadeIn">
                            {renderTabContent(plans[activeTab])}
                        </div>
                    )}

                    {/* FAQ or Additional Info */}
                    {!isLoading && !error && Object.keys(plans).length > 0 && (
                        <div className="mt-16 max-w-4xl mx-auto">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Need help choosing?</h3>
                                <p className="text-sm text-gray-700 mb-4">
                                    All plans include secure HMRC integration, real-time tax calculations, and expert support.
                                    Start with our basic plan and upgrade anytime as your needs grow.
                                </p>
                                <p className="text-xs text-gray-600">
                                    Questions? Contact our support team for personalized recommendations.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AuthenticatedLayout>
    )
}
